import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Globe2, Check, Plus, MapPin } from "lucide-react";

const COUNTRY_NAMES = { IN: "India", US: "United States" };

export default function Territories() {
  const [zones, setZones] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [country, setCountry] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const loadZones = () => api.get("/global-admin/geo").then(r => setZones(r.data)).catch(() => {});
  const loadReqs = () => api.get("/schools/plan-change-requests", { params: { scope: "global" } }).then(r => setReqs(r.data)).catch(() => {});
  useEffect(() => { loadZones(); loadReqs(); }, []);

  const toggleOverride = async (z) => {
    try {
      await api.patch(`/global-admin/geo/${z.geo_id}/override`, { allow_override: !z.allow_override });
      toast.success(`Override ${!z.allow_override ? "enabled" : "disabled"}`); loadZones();
    } catch { toast.error("Failed"); }
  };

  const approve = async (rid) => {
    try {
      const r = await api.post(`/global-admin/plan-change-requests/${rid}/approve`);
      toast.success(r.data.idempotent ? "Already processed" : `Approved → ${r.data.new_plan}`);
      loadReqs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const countries = [...new Set(zones.map(z => z.country_code))];
  const shown = (country ? zones.filter(z => z.country_code === country) : zones)
    .sort((a, b) => (a.country_code + a.state + a.district + a.city).localeCompare(b.country_code + b.state + b.district + b.city));
  const pending = reqs.filter(r => r.status === "PENDING");

  return (
    <div className="space-y-6">
      {/* Plan change requests (unchanged) */}
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-[#FDE047] flex items-center justify-between">
          <h3 className="display text-lg font-bold">Plan Change Requests</h3>
          <span className="nb-badge bg-white text-xs">{pending.length} pending</span>
        </div>
        <table className="nb-table">
          <thead><tr><th>School</th><th>Current</th><th>Requested</th><th>Remark</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>
            {reqs.map(r => (
              <tr key={r.request_id} data-testid={`plan-req-${r.request_id}`}>
                <td className="font-bold">{r.school_name}</td>
                <td className="mono text-xs">{r.current_plan || "—"}</td>
                <td className="mono text-xs font-bold">{r.requested_plan}</td>
                <td className="text-xs">{r.remark || "—"}</td>
                <td><span className={`nb-badge ${r.status === "APPROVED" ? "bg-[#86EFAC]" : "bg-[#FDE047]"}`}>{r.status}</span></td>
                <td>{r.status === "PENDING"
                  ? <button className="nb-btn text-xs" onClick={() => approve(r.request_id)} data-testid={`plan-req-approve-${r.request_id}`}>Approve</button>
                  : <span className="text-xs text-slate-400">—</span>}</td>
              </tr>
            ))}
            {reqs.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No requests yet</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Hierarchical territories */}
      <div className="nb-card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b-2 border-black bg-white flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2"><Globe2 size={20} /><h3 className="display text-lg font-bold">Territories ({shown.length})</h3></div>
          <div className="flex gap-2 items-center">
            <button className="nb-btn text-xs" onClick={() => setAddOpen(true)} data-testid="terr-add-btn"><Plus size={13} /> Add / Manage Location</button>
            <select className="nb-select text-xs" value={country} onChange={e => setCountry(e.target.value)} data-testid="terr-country-filter">
              <option value="">All countries</option>
              {countries.map(c => <option key={c} value={c}>{COUNTRY_NAMES[c] || c}</option>)}
            </select>
          </div>
        </div>
        <table className="nb-table">
          <thead><tr><th>Country</th><th>State</th><th>District</th><th>City</th><th>Pincode</th><th>Actions</th></tr></thead>
          <tbody>
            {shown.map(z => (
              <tr key={z.geo_id} data-testid={`terr-row-${z.geo_id}`}>
                <td className="mono text-xs">{z.country_code}</td>
                <td className="font-bold">{z.state || "—"}</td>
                <td>{z.district || <span className="text-slate-300">—</span>}</td>
                <td>{z.city || <span className="text-slate-300">—</span>}</td>
                <td className="mono text-xs">{z.postal_code || <span className="text-slate-300">—</span>}</td>
                <td>
                  {z.postal_code
                    ? <button onClick={() => toggleOverride(z)} className={`nb-btn text-xs ${z.allow_override ? "" : "nb-btn-ghost"}`} data-testid={`terr-override-${z.geo_id}`}>
                        Override {z.allow_override ? "ON" : "OFF"}
                      </button>
                    : <span className="nb-badge bg-[#Eef2ff] text-[10px]">{z.level || "state"}</span>}
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-slate-500">No locations</td></tr>}
          </tbody>
        </table>
      </div>

      {addOpen && <AddLocationDrawer onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); loadZones(); }} />}
    </div>
  );
}

function AddLocationDrawer({ onClose, onSaved }) {
  const [cc, setCc] = useState("IN");
  const [states, setStates] = useState([]);
  const [stateCode, setStateCode] = useState("");
  const [districts, setDistricts] = useState([]);
  const [district, setDistrict] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const [addState, setAddState] = useState(false);
  const [nsCode, setNsCode] = useState("");
  const [nsName, setNsName] = useState("");
  const [addDistrict, setAddDistrict] = useState(false);
  const [ndName, setNdName] = useState("");
  const [busy, setBusy] = useState(false);

  const stateName = states.find(s => s.state_code === stateCode)?.state_name || "";

  const loadStates = () => api.get("/geo/states-full", { params: { country_code: cc } }).then(r => setStates(r.data)).catch(() => setStates([]));
  useEffect(() => { setStateCode(""); setDistricts([]); setDistrict(""); loadStates(); }, [cc]);
  useEffect(() => {
    setDistrict(""); setDistricts([]);
    if (stateName) api.get("/geo/districts", { params: { country_code: cc, state: stateName } }).then(r => setDistricts(r.data)).catch(() => setDistricts([]));
  }, [stateCode]); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      let sc = stateCode, sn = stateName;
      if (addState) {
        if (!nsCode.trim() || !nsName.trim()) { toast.error("Enter new state code + name"); setBusy(false); return; }
        await api.post("/global-admin/geo/state", { country_code: cc, country_name: COUNTRY_NAMES[cc] || cc, state_code: nsCode.trim().toUpperCase(), state: nsName.trim() });
        sc = nsCode.trim().toUpperCase(); sn = nsName.trim();
        toast.success(`State "${sn}" added`);
      }
      if (!sc) { toast.error("Select or add a state"); setBusy(false); return; }
      let dist = district;
      if (addDistrict) {
        if (!ndName.trim()) { toast.error("Enter new district name"); setBusy(false); return; }
        await api.post("/global-admin/geo/district", { country_code: cc, country_name: COUNTRY_NAMES[cc] || cc, state_code: sc, state: sn, district: ndName.trim() });
        dist = ndName.trim();
        toast.success(`District "${dist}" added`);
      }
      if (city.trim() && pincode.trim()) {
        if (!dist) { toast.error("Pick a district for this city"); setBusy(false); return; }
        await api.post("/global-admin/geo", { country_code: cc, country_name: COUNTRY_NAMES[cc] || cc, state: sn, district: dist, city: city.trim(), postal_code: pincode.trim(), metro_tier: "TIER_2" });
        toast.success(`Locality ${city} (${pincode}) added`);
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="nb-card bg-white p-5 max-w-lg w-full space-y-3 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="terr-add-modal">
        <div className="flex items-center gap-2"><MapPin size={18} /><h3 className="display text-lg font-bold">Add / Manage Location</h3></div>

        <label className="block"><span className="nb-label text-[11px]">Country</span>
          <select className="nb-select mt-1" value={cc} onChange={e => setCc(e.target.value)} data-testid="terr-add-country">
            <option value="IN">India</option><option value="US">United States</option>
          </select></label>

        <div>
          <div className="flex items-center justify-between">
            <span className="nb-label text-[11px]">State</span>
            <button className="text-[11px] underline" onClick={() => setAddState(v => !v)} data-testid="terr-toggle-new-state">{addState ? "Pick existing" : "+ Add New State"}</button>
          </div>
          {addState ? (
            <div className="grid grid-cols-3 gap-2 mt-1">
              <input className="nb-input mono" placeholder="Code" value={nsCode} onChange={e => setNsCode(e.target.value)} data-testid="terr-new-state-code" />
              <input className="nb-input col-span-2" placeholder="State name" value={nsName} onChange={e => setNsName(e.target.value)} data-testid="terr-new-state-name" />
            </div>
          ) : (
            <select className="nb-select mt-1" value={stateCode} onChange={e => setStateCode(e.target.value)} data-testid="terr-add-state">
              <option value="">Select state…</option>
              {states.map(s => <option key={s.state_code} value={s.state_code}>{s.state_name}</option>)}
            </select>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between">
            <span className="nb-label text-[11px]">District</span>
            <button className="text-[11px] underline" onClick={() => setAddDistrict(v => !v)} data-testid="terr-toggle-new-district">{addDistrict ? "Pick existing" : "+ Add New District"}</button>
          </div>
          {addDistrict ? (
            <input className="nb-input mt-1" placeholder="New district name" value={ndName} onChange={e => setNdName(e.target.value)} data-testid="terr-new-district-name" />
          ) : (
            <>
              <select className="nb-select mt-1" value={district} onChange={e => setDistrict(e.target.value)} disabled={!stateCode || addState} data-testid="terr-add-district">
                <option value="">Select district…</option>
                {districts.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
              {stateCode && !addState && districts.length === 0 &&
                <p className="text-xs text-amber-700 mt-1">No districts added yet — use "+ Add New District".</p>}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="nb-label text-[11px]">City (optional)</span>
            <input className="nb-input mt-1" placeholder="City" value={city} onChange={e => setCity(e.target.value)} data-testid="terr-add-city" /></label>
          <label className="block"><span className="nb-label text-[11px]">Pincode (optional)</span>
            <input className="nb-input mt-1 mono" placeholder="Postal code" value={pincode} onChange={e => setPincode(e.target.value)} data-testid="terr-add-postal" /></label>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button className="nb-btn nb-btn-ghost text-xs" onClick={onClose}>Cancel</button>
          <button className="nb-btn text-xs" disabled={busy} onClick={save} data-testid="terr-add-save"><Check size={13} /> Save Location</button>
        </div>
      </div>
    </div>
  );
}
